// useDashboardForm
// State for the "Add + Assign Dashboard" form in ConsoleAdmin.

import { useCallback, useState } from 'react';
import { ConsoleDashboardSummary } from '../../../types';

// Everything the form edits.
export interface DashboardForm {
  dashboardId: string; // '' while creating a new dashboard
  name: string;
  description: string;
  sourceLink: string;
  primaryDeveloper: string;
  secondaryDeveloper: string;
  userManagerAdmin: string;
}

// Starting a new dashboard (after a save, or choosing "Create new dashboard")
// always resets to EMPTY_DASHBOARD_FORM, and editing always fills the form
// from dashboardToForm. Both are typed as the whole DashboardForm, so a field
// added later can't be left out of either: TypeScript won't compile. (The
// User Manager Admin was once added to "fill" but not to "clear", so a new
// dashboard inherited the previous one's.)
export const EMPTY_DASHBOARD_FORM: DashboardForm = {
  dashboardId: '',
  name: '',
  description: '',
  sourceLink: '',
  primaryDeveloper: '',
  secondaryDeveloper: '',
  userManagerAdmin: '',
};

export const dashboardToForm = (dashboard: ConsoleDashboardSummary): DashboardForm => ({
  dashboardId: dashboard.dashboard_id,
  name: dashboard.name || '',
  description: dashboard.description || '',
  sourceLink: dashboard.source_link || '',
  primaryDeveloper: dashboard.primary_developer_email || '',
  secondaryDeveloper: dashboard.secondary_developer_email || '',
  userManagerAdmin: dashboard.authorized_users_manager_email || '',
});

export const useDashboardForm = (dashboards: ConsoleDashboardSummary[]) => {
  const [form, setForm] = useState<DashboardForm>(EMPTY_DASHBOARD_FORM);

  const setField = useCallback((field: keyof DashboardForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const startNew = useCallback(() => setForm(EMPTY_DASHBOARD_FORM), []);

  const edit = useCallback((dashboard: ConsoleDashboardSummary) => setForm(dashboardToForm(dashboard)), []);

  // The "Existing Dashboard" picker: '' is "Create new dashboard".
  const selectExisting = (dashboardId: string) => {
    const existing = dashboardId ? dashboards.find((d) => d.dashboard_id === dashboardId) : undefined;
    if (existing) edit(existing);
    else startNew();
  };

  return { form, setField, startNew, edit, selectExisting };
};
